class DeliveryInfoComponent extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
        return ['primary-text', 'secondary-text', 'link-text', 'link-url', 'show-link', 'design-variant', 'text-style', 'icon-color'];
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback() {
        if (this.shadowRoot.innerHTML) {
            this.render();
        }
    }

    get primaryText() {
        return this.getAttribute('primary-text') || '';
    }

    get secondaryText() {
        return this.getAttribute('secondary-text') || '';
    }

    get linkText() {
        return this.getAttribute('link-text') || '';
    }

    get linkUrl() {
        return this.getAttribute('link-url') || '';
    }

    get showLink() {
        return this.getAttribute('show-link') === 'true';
    }

    get designVariant() {
        return this.getAttribute('design-variant') || 'design_a';
    }

    get textStyle() {
        return this.getAttribute('text-style') || '';
    }

    get iconColor() {
        return this.getAttribute('icon-color') || 'rgb(62, 214, 96)';
    }

    getStyles() {
        const baseStyles = `
      :host {
        display: block;
        font-family: inherit;
      }

      * {
        box-sizing: border-box;
      }

      .delivery-info {
        margin-top: 1.5rem;
        margin-bottom: 1.5rem;
      }

      .delivery-text {
        margin: 0;
        padding: 0;
      }

      .icon-wrapper {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .icon-wrapper svg {
        width: 100%;
        height: 100%;
      }

      .delivery-link {
        cursor: pointer;
      }

      /* Text Style Variants */
      .caption-with-letter-spacing .delivery-text {
        text-transform: uppercase;
        letter-spacing: 0.1em;
        font-size: 0.85rem;
      }

      .subtitle .delivery-text {
        font-size: 1rem;
        font-weight: 400;
        color: #666;
      }
    `;

        const variantStyles = {
            design_a: `
        .delivery-info {
          padding: 1.2rem 1.5rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
        }

        .delivery-info:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border-color: #d0d0d0;
        }

        .delivery-text {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem 0;
          font-size: 1.2rem;
          line-height: 1.5;
          color: #2c3e50;
          font-weight: 500;
        }

        .delivery-text:first-of-type {
          padding-top: 0;
        }

        .delivery-text:last-of-type {
          padding-bottom: 0;
        }

        .icon-wrapper {
          width: 20px;
          height: 20px;
        }

        .delivery-text + .delivery-text {
          border-top: 1px solid #f0f0f0;
          margin-top: 0.5rem;
          padding-top: 0.75rem;
        }

        .delivery-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding: 0.5rem 0;
          font-size: 0.9rem;
          color: #2c3e50;
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
          border-bottom: 1px solid transparent;
        }

        .delivery-link:hover {
          color: #000;
          border-bottom-color: #000;
        }

        .delivery-link::after {
          content: '→';
          font-size: 1.1rem;
          transition: transform 0.2s ease;
        }

        .delivery-link:hover::after {
          transform: translateX(4px);
        }

        @media screen and (max-width: 749px) {
          .delivery-info {
            margin-top: 1rem;
            padding: 1rem;
            border-radius: 6px;
          }

          .delivery-text {
            font-size: 1.2rem;
          }

          .delivery-link {
            font-size: 0.85rem;
          }
        }

        @media screen and (min-width: 750px) and (max-width: 989px) {
          .delivery-info {
            padding: 1.1rem 1.3rem;
          }
        }
      `,

            design_b: `
        .delivery-info {
          display: grid;
          padding: 1rem 0;
          border-top: 1px solid #e0e0e0;
          margin-bottom: unset;
        }

        .delivery-text {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0.5rem 0;
          font-size: 1.2rem;
          color: #2c3e50;
          font-weight: 400;
        }

        .icon-wrapper {
          width: 18px;
          height: 18px;
        }

        .delivery-link {
          display: inline-block;
          margin-top: 0.75rem;
          font-size: 0.9rem;
          color: #2c3e50;
          text-decoration: underline;
          text-underline-offset: 3px;
          transition: color 0.2s ease;
        }

        .delivery-link:hover {
          color: #000;
        }

        @media screen and (max-width: 749px) {
          .delivery-info {
            margin-top: 1rem;
            padding: 0.75rem 0;
          }

          .delivery-text {
            font-size: 1.2rem;
          }
        }
        .delivery-text.primary {
          order: ${ this.getAttribute('dispatch_by_post') === "true" ? 2 : 1 };
        }
        .delivery-text.secondary {
          order: ${ this.getAttribute('dispatch_by_post') === "true" ? 1 : 2 };
        }
        .delivery-link {
          order: 3;
        }
      `,

            design_c: `
        .delivery-info {
          padding: 1.25rem 1.5rem;
          background: #ffffff;
          border: 1px solid #e8e8e8;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .delivery-text {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0.5rem 0;
          font-size: 1.2rem;
          color: #333;
          font-weight: 400;
        }

        .delivery-text:first-of-type {
          font-weight: 600;
          color: #3eb160;
        }

        .icon-wrapper {
          width: 20px;
          height: 20px;
        }

        .delivery-link {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 1rem;
          padding: 0.5rem 1rem;
          background: #f8f9fa;
          border-radius: 6px;
          font-size: 0.9rem;
          color: #2c3e50;
          text-decoration: none;
          transition: background 0.2s ease;
        }

        .delivery-link:hover {
          background: #e9ecef;
        }

        @media screen and (max-width: 749px) {
          .delivery-info {
            margin-top: 1rem;
            padding: 1rem;
            border-radius: 8px;
          }
        }
      `,

            design_d: `
        .delivery-info {
          padding: 1.25rem 1.5rem;
          background: #f8f9fa;
          border-left: 4px solid #3eb160;
          border-radius: 4px;
        }

        .delivery-text {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin: 0.75rem 0;
          font-size: 1.2rem;
          line-height: 1.6;
          color: #2c3e50;
          font-weight: 400;
        }
        .delivery-text.primary {
          order: ${ this.getAttribute('dispatch_by_post') === "true" ? 2 : 1 };
        }
        .delivery-text.secondary {
          order: ${ this.getAttribute('dispatch_by_post') === "true" ? 1 : 2 };
        }
        .delivery-text:first-child {
          margin-top: 0;
        }

        .delivery-text:last-child {
          margin-bottom: 0;
        }

        .icon-wrapper {
          width: 24px;
          height: 24px;
          background: #3eb160;
          border-radius: 50%;
          padding: 4px;
        }

        .icon-wrapper svg {
          filter: brightness(0) invert(1);
        }

        .delivery-link {
          display: inline-block;
          margin-top: 1rem;
          font-size: 0.9rem;
          color: #2c3e50;
          text-decoration: underline;
          text-underline-offset: 3px;
          font-weight: 500;
        }

        .delivery-link:hover {
          color: #3eb160;
        }

        @media screen and (max-width: 749px) {
          .delivery-info {
            margin-top: 1rem;
            padding: 1rem;
          }

          .delivery-text {
            font-size: 0.9rem;
          }
        }
      `
        };

        console.log(`this.getAttribute('dispatch_by_post') === "true"`, this.getAttribute('dispatch_by_post') === "true")

        return baseStyles + (variantStyles[this.designVariant] || variantStyles.design_a);
    }

    getIcon() {
        return `
            <span class="icon-wrapper" style="color: ${this.iconColor}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <circle cx="10" cy="10" r="8" fill="currentColor" opacity="0.2"/>
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" fill="currentColor"/>
              </svg>
            </span>`
    }

    render() {
        const textStyleClass = this.textStyle ? this.textStyle : '';

        this.shadowRoot.innerHTML = `
      <style>${this.getStyles()}</style>
      
      <div class="delivery-info ${textStyleClass}">
        ${this.primaryText ? `
          <p class="delivery-text primary">
            ${this.getIcon()}
            ${this.primaryText}
          </p>
        ` : ''}
        
        ${this.secondaryText ? `
          <p class="delivery-text secondary">
            ${this.getIcon()}
            ${this.secondaryText}
          </p>
        ` : ''}
        
        ${this.showLink && this.linkUrl ? `
          <a href="${this.linkUrl}" class="delivery-link">${this.linkText}</a>
        ` : ''}
      </div>
    `;
    }
}

// Register the custom element
if (!customElements.get('delivery-info')) {
    customElements.define('delivery-info', DeliveryInfoComponent);
}
